import React, { useState } from "react";
import SourceChip from "../section/SourceChip.jsx";
import api from "../../api/client.js";
import AiPill from "./AiPill.jsx";
import AutoTextarea from "../AutoTextarea.jsx";

const SUBTYPES_BY_CATEGORY = {
  sensory:       ["love", "comfort", "distress"],
  communication: ["phrase", "aac", "yes", "no", "done", "overwhelm_early"],
  support:       ["early_sign", "trigger", "what_helps", "what_makes_worse", "after_care"],
  // Passions and Community had no category, and category is required, so
  // "Add item" on either section was a 400 until Sep 28.
  passion:       ["interest", "activity", "comfort_object"],
  community:     ["place", "activity", "group"],
};

const CATEGORY_LABELS = {
  sensory: "Sensory", communication: "Communication", support: "Support",
  passion: "Passion", community: "Community",
};

const SUBTYPE_LABELS = {
  love: "Loves", comfort: "Comfort", distress: "Distress",
  phrase: "Key phrase", aac: "AAC / tablet",
  yes: "How she says yes", no: "How she says no", done: "How she says done",
  overwhelm_early: "Early overwhelm sign",
  early_sign: "Early sign", trigger: "Trigger",
  what_helps: "What helps", what_makes_worse: "What makes it worse",
  after_care: "After care",
  interest: "Interest", activity: "Activity", comfort_object: "Comfort object",
  place: "Place", group: "Group",
};

const SENSES = ["sight", "sound", "touch", "taste", "smell", "interoception", "other"];

function ItemRow({ item, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/items/${item.id}`, {
        title: form.title,
        description: form.description,
        example: form.example || null,
        critical: form.critical,
        sense: form.sense || null,
      });
      onSave({ ...item, ...form });
      setEditing(false);
    } catch (e) {
      alert(e.body?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete "${item.title}"?`)) return;
    await api.delete(`/items/${item.id}`);
    onDelete(item.id);
  }

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  if (!editing) {
    return (
      <div style={styles.row}>
        <div style={styles.rowMain}>
          {item.critical && <span style={styles.critTag}>Critical</span>}
          <span style={styles.rowTitle}>{item.title}</span>
          <span style={styles.rowSubtype}>{SUBTYPE_LABELS[item.subtype] || item.subtype}</span>
          <SourceChip source={item.source} />
        </div>
        <div style={styles.rowActions}>
          <AiPill type="draft" />
          <button style={styles.editBtn} onClick={() => setEditing(true)}>Edit</button>
          <button style={styles.deleteBtn} onClick={remove}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.editCard}>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Title</label>
        <input style={styles.input} value={form.title} onChange={f("title")} />
      </div>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Description</label>
        <AutoTextarea style={styles.textarea} value={form.description} onChange={f("description")} rows={3} />
      </div>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Example (optional)</label>
        <input style={styles.input} value={form.example || ""} onChange={f("example")} placeholder="A past instance…" />
      </div>
      {item.category === "sensory" && (
        <div style={styles.fieldRow}>
          <label style={styles.label}>Sense</label>
          <select style={styles.select} value={form.sense || ""} onChange={f("sense")}>
            <option value="">—</option>
            {SENSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      <label style={styles.checkRow}>
        <input type="checkbox" checked={form.critical} onChange={f("critical")} />
        <span style={styles.checkLabel}>Critical item</span>
      </label>
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button style={styles.cancelBtn} onClick={() => { setForm({ ...item }); setEditing(false); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddItemForm({ clientId, sectionType, sectionCategory, onAdded }) {
  const defaultCategory = sectionCategory || "support";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    category: defaultCategory,
    subtype: SUBTYPES_BY_CATEGORY[defaultCategory]?.[0] || "trigger",
    title: "", description: "", example: "", critical: false, sense: "",
  });
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(p => {
      const next = { ...p, [k]: val };
      if (k === "category") next.subtype = SUBTYPES_BY_CATEGORY[val]?.[0] || "";
      return next;
    });
  };

  async function add() {
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    try {
      const res = await api.post(`/clients/${clientId}/sections/${sectionType}/items`, {
        category: form.category,
        subtype: form.subtype,
        title: form.title,
        description: form.description,
        example: form.example || null,
        critical: form.critical,
        sense: form.sense || null,
      });
      onAdded(res.id);
      setForm({ ...form, title: "", description: "", example: "", critical: false });
      setOpen(false);
    } catch (e) {
      alert(e.body?.error || "Add failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button style={styles.addBtn} onClick={() => setOpen(true)}>+ Add item</button>
    );
  }

  return (
    <div style={styles.editCard}>
      <div style={styles.twoCol}>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Category</label>
          {/* A section owns one category; the select is only for the three
              categories that share editors. Passions and Community are fixed. */}
          {["passion", "community"].includes(defaultCategory) ? (
            <div style={{ ...styles.select, background: "var(--color-bg-subtle)" }}>{CATEGORY_LABELS[form.category]}</div>
          ) : (
            <select style={styles.select} value={form.category} onChange={f("category")}>
              <option value="sensory">Sensory</option>
              <option value="communication">Communication</option>
              <option value="support">Support</option>
            </select>
          )}
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Subtype</label>
          <select style={styles.select} value={form.subtype} onChange={f("subtype")}>
            {(SUBTYPES_BY_CATEGORY[form.category] || []).map(s => (
              <option key={s} value={s}>{SUBTYPE_LABELS[s] || s}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Title</label>
        <input style={styles.input} value={form.title} onChange={f("title")} placeholder="Short label" />
      </div>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Description</label>
        <AutoTextarea style={styles.textarea} value={form.description} onChange={f("description")} rows={3} placeholder="What a DSP needs to know" />
      </div>
      <div style={styles.fieldRow}>
        <label style={styles.label}>Example (optional)</label>
        <input style={styles.input} value={form.example} onChange={f("example")} placeholder="A past instance…" />
      </div>
      <label style={styles.checkRow}>
        <input type="checkbox" checked={form.critical} onChange={f("critical")} />
        <span style={styles.checkLabel}>Critical item</span>
      </label>
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={add} disabled={saving || !form.title.trim()}>
          {saving ? "Adding…" : "Add item"}
        </button>
        <button style={styles.cancelBtn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function ItemEditor({ clientId, sectionType, sectionCategory, initialItems = [], onRefresh }) {
  const [items, setItems] = useState(initialItems);

  function handleSave(updated) {
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
  }
  function handleDelete(id) {
    setItems(prev => prev.filter(i => i.id !== id));
  }
  function handleAdded() {
    onRefresh(); // re-fetch from server
  }

  return (
    <div style={styles.root}>
      <h3 style={styles.heading}>Items</h3>
      {items.map(item => (
        <ItemRow key={item.id} item={item} onSave={handleSave} onDelete={handleDelete} />
      ))}
      <AddItemForm
        clientId={clientId}
        sectionType={sectionType}
        sectionCategory={sectionCategory}
        onAdded={handleAdded}
      />
    </div>
  );
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  heading: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "var(--color-text-tertiary)",
    paddingBottom: "var(--space-2)",
    borderBottom: "1px solid var(--color-border)",
  },
  row: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3) var(--space-4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-4)",
  },
  rowMain: { display: "flex", alignItems: "center", gap: "var(--space-3)", flex: 1, minWidth: 0, flexWrap: "wrap" },
  critTag: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-critical)",
    background: "var(--color-critical-bg)",
    borderRadius: "var(--radius-full)",
    padding: "1px var(--space-2)",
    whiteSpace: "nowrap",
  },
  rowTitle: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-primary)",
  },
  rowSubtype: {
    fontSize: "var(--text-xs)",
    color: "var(--color-text-tertiary)",
    whiteSpace: "nowrap",
  },
  rowActions: { display: "flex", alignItems: "center", gap: "var(--space-2)", flexShrink: 0 },
  editBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "pointer" },
  deleteBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "2px var(--space-2)" },
  editCard: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-5)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" },
  fieldRow: { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  label: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-tertiary)" },
  input: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%" },
  textarea: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", resize: "vertical", fontFamily: "inherit" },
  select: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)" },
  checkRow: { display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" },
  checkLabel: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  editActions: { display: "flex", gap: "var(--space-3)" },
  saveBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  cancelBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  addBtn: { fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start" },
};
